import type { PerfEvent } from '@logclip/types';
import type { InstrumentCtx, Teardown } from './context.js';
import { uuid } from './util.js';

// Lightweight web-vitals capture via PerformanceObserver — no extra dependency.
// Emits LCP, CLS, INP (approx), FCP, TTFB, and a navigation summary (§5/§11).

function rate(metric: PerfEvent['metric'], value: number): PerfEvent['rating'] {
  const t: Partial<Record<PerfEvent['metric'], [number, number]>> = {
    LCP: [2500, 4000],
    INP: [200, 500],
    CLS: [0.1, 0.25],
    FCP: [1800, 3000],
    TTFB: [800, 1800],
  };
  const pair = t[metric];
  if (!pair) return undefined;
  if (value <= pair[0]) return 'good';
  if (value <= pair[1]) return 'needs-improvement';
  return 'poor';
}

export function instrumentVitals(ctx: InstrumentCtx): Teardown {
  if (typeof PerformanceObserver === 'undefined') return () => {};
  const observers: PerformanceObserver[] = [];

  const emit = (metric: PerfEvent['metric'], value: number, detail?: Record<string, number>) => {
    const e: PerfEvent = {
      id: uuid(),
      sessionId: ctx.sessionId,
      type: 'perf',
      t: ctx.clock.now(),
      ts: ctx.clock.wall(),
      metric,
      value: Math.round(value * 1000) / 1000,
      rating: rate(metric, value),
      detail,
    };
    ctx.emit(e);
  };

  const obs = (type: string, cb: (entries: PerformanceEntryList) => void, extra?: object) => {
    try {
      const o = new PerformanceObserver((list) => cb(list.getEntries()));
      o.observe({ type, buffered: true, ...extra });
      observers.push(o);
    } catch {
      /* unsupported */
    }
  };

  // FCP
  obs('paint', (entries) => {
    for (const e of entries) if (e.name === 'first-contentful-paint') emit('FCP', e.startTime);
  });

  // LCP — keep the largest seen.
  let lcp = 0;
  obs('largest-contentful-paint', (entries) => {
    const last = entries[entries.length - 1];
    if (last) lcp = last.startTime;
  });

  // CLS — accumulate session layout shift.
  let cls = 0;
  obs('layout-shift', (entries) => {
    for (const e of entries as (PerformanceEntry & { value: number; hadRecentInput: boolean })[]) {
      if (!e.hadRecentInput) cls += e.value;
    }
  });

  // INP (approx via event timing — track max interaction delay).
  let inp = 0;
  obs(
    'event',
    (entries) => {
      for (const e of entries as (PerformanceEntry & { duration: number })[]) {
        if (e.duration > inp) inp = e.duration;
      }
    },
    { durationThreshold: 40 },
  );

  // Navigation timing → TTFB + a navigation summary.
  obs('navigation', (entries) => {
    const nav = entries[0] as PerformanceNavigationTiming | undefined;
    if (!nav) return;
    emit('TTFB', nav.responseStart);
    emit('navigation', nav.duration, {
      domInteractive: Math.round(nav.domInteractive),
      domComplete: Math.round(nav.domComplete),
      loadEvent: Math.round(nav.loadEventEnd),
    });
  });

  // Flush LCP/CLS/INP on the way out (their final values).
  const flushFinal = () => {
    if (lcp) emit('LCP', lcp);
    emit('CLS', cls);
    if (inp) emit('INP', inp);
  };
  const onHide = () => {
    if (document.visibilityState === 'hidden') flushFinal();
  };
  document.addEventListener('visibilitychange', onHide, true);

  return () => {
    observers.forEach((o) => o.disconnect());
    document.removeEventListener('visibilitychange', onHide, true);
  };
}
